import React from 'react';
import SimpleAdmin, { DefaultOptions } from 'components/simpleAdmin';
import { useDispatch, useSelector } from 'react-redux';
import { apiDelete, apiGet, apiPatch, apiPost } from 'services';
import { Loading, SystemText } from '@salesperformanceint/e4shared';
import { handleError } from 'actions/admin';
import { setShowSuccess } from 'actions/ui';

export const AdminCompetencyRatingGroups = ({ history }) => {
    const dispatch = useDispatch();

    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    const user = useSelector(state => state.user.user);
    const company = user.companySettings;

    if (!user.sys_Config) history.replace('/');
    if (!user.companySettings.development.usePositiveIndicators) {
        history.replace('/error');
    }

    const rating = company?.display?.ratingRange || 5;

    const translations = {
        columnCompetencies: SystemText('Admin/CompetencyRatingGroups/Table/Columns/Competencies'),
        columnName: SystemText('Admin/CompetencyRatingGroups/Table/Columns/Name'),
        columnType: SystemText('Admin/CompetencyRatingGroups/Table/Columns/Type'),
        helpName: SystemText('Admin/CompetencyRatingGroups/HelpText/Name'),
        helpUseMetrics: SystemText('Admin/CompetencyRatingGroups/HelpText/UseMetrics'),
        helpOption: x => SystemText('Admin/CompetencyRatingGroups/HelpText/Option', x),
        helpRange: SystemText('Admin/CompetencyRatingGroups/HelpText/Range'),
        metric: SystemText('Admin/CompetencyRatingGroups/RatingGroupTypes/Metric'),
        name: SystemText('Admin/CompetencyRatingGroups/FormFields/Name'),
        option: SystemText('Admin/CompetencyRatingGroups/Option'),
        range: SystemText('Admin/CompetencyRatingGroups/Range'),
        text: SystemText('Admin/CompetencyRatingGroups/RatingGroupTypes/Text'),
        title: SystemText('Admin/CompetencyRatingGroups/Title'),
        success: SystemText('Admin/CompetencyRatingGroups/Success'),
        titlePlural: SystemText('Admin/CompetencyRatingGroups/Title/Plural'),
        useMetrics: SystemText('Admin/CompetencyRatingGroups/FormFields/UseMetrics'),
        validationMessage2: SystemText('Admin/CompetencyRatingGroups/ValidationMessages/Range2'),
        validationMessage3: SystemText('Admin/CompetencyRatingGroups/ValidationMessages/Range3'),
        validationMessage4: SystemText('Admin/CompetencyRatingGroups/ValidationMessages/Range4'),
        validationMessage5: SystemText('Admin/CompetencyRatingGroups/ValidationMessages/Range5'),
    };

    const ratingGroupTypes = [
        {
            id: 0,
            ref: 'Text',
            text: translations.text,
        },
        {
            id: 1,
            ref: 'Metrics',
            text: translations.metric,
        },
    ];

    const lessThan = (value1, value2) => parseInt(value1, 10) < parseInt(value2, 10);

    const fifthOptions = [
        {
            label: `${translations.option} 5`,
            type: 'text',
            dataProperty: 'option5',
            mandatory: true,
            maxLength: 300,
            generalErrors: true,
            help: translations.helpOption(5),
        },
        {
            label: `${translations.range} 5 (%)`,
            type: 'number',
            dataProperty: 'range5',
            mandatory: true,
            toggleProperty: 'useMetrics',
            generalErrors: true,
            help: translations.helpRange,
            validation: entity =>
                entity.useMetrics && lessThan(entity.range5, entity.range4) ? translations.validationMessage5 : '',
        },
    ];

    const formFields = [
        {
            label: translations.name,
            type: 'text',
            dataProperty: 'name',
            mandatory: true,
            maxLength: 50,
            generalErrors: true,
            help: translations.helpName,
        },
        {
            label: translations.useMetrics,
            type: 'toggle',
            dataProperty: 'useMetrics',
            maxLength: 50,
            help: translations.helpUseMetrics,
        },
        {
            label: `${translations.option} 1`,
            type: 'text',
            dataProperty: 'option1',
            mandatory: true,
            maxLength: 300,
            help: translations.helpOption(1),
        },
        {
            label: `${translations.range} 1 (%)`,
            type: 'number',
            dataProperty: 'range1',
            toggleProperty: 'useMetrics',
            mandatory: true,
            readonly: true,
            help: translations.helpRange,
        },
        {
            label: `${translations.option} 2`,
            type: 'text',
            dataProperty: 'option2',
            mandatory: true,
            maxLength: 300,
            help: translations.helpOption(2),
        },
        {
            label: `${translations.range} 2 (%)`,
            type: 'number',
            dataProperty: 'range2',
            toggleProperty: 'useMetrics',
            mandatory: true,
            help: translations.helpRange,
            validation: entity =>
                entity.useMetrics && lessThan(entity.range2, entity.range1) ? translations.validationMessage2 : '',
        },
        {
            label: `${translations.option} 3`,
            type: 'text',
            dataProperty: 'option3',
            mandatory: true,
            maxLength: 300,
            help: translations.helpOption(3),
        },
        {
            label: `${translations.range} 3 (%)`,
            type: 'number',
            dataProperty: 'range3',
            mandatory: true,
            toggleProperty: 'useMetrics',
            help: translations.helpRange,
            validation: entity =>
                entity.useMetrics && lessThan(entity.range3, entity.range2) ? translations.validationMessage3 : '',
        },
        {
            label: `${translations.option} 4`,
            type: 'text',
            dataProperty: 'option4',
            mandatory: true,
            maxLength: 300,
            help: translations.helpOption(4),
        },
        {
            label: `${translations.range} 4 (%)`,
            type: 'number',
            dataProperty: 'range4',
            mandatory: true,
            toggleProperty: 'useMetrics',
            help: translations.helpRange,
            validation: entity =>
                entity.useMetrics && lessThan(entity.range4, entity.range3) ? translations.validationMessage4 : '',
        },
    ];

    if (rating === 5 && formFields.length === 10) {
        fifthOptions.forEach(f => formFields.push(f));
    }

    const api = {
        load: () => {
            setLoading(true);
            apiGet(
                `competency-rating-groups`,
                user.guid,
                response => {
                    const respData = response.map(group => {
                        const ratingGroupTypeText = ratingGroupTypes.find(r => r.id === group.ratingGroupType).ref;

                        return {
                            ...group,
                            ratingGroupType: ratingGroupTypeText,
                            useMetrics: ratingGroupTypeText === 'Metrics',
                        };
                    });
                    setData(respData);
                    setLoading(false);
                },
                error => {
                    handleError(dispatch, error);
                }
            );
        },
        save: (group, callback) => {
            group.ratingGroupType = group.useMetrics
                ? ratingGroupTypes.find(r => r.ref === 'Metrics').id
                : ratingGroupTypes.find(r => r.ref === 'Text').id;
            apiPost('competency-rating-groups', group, user.guid, () => {
                dispatch(setShowSuccess(translations.success));
                api.load();
                callback?.();
            });
        },
        update: (group, callback) => {
            group.ratingGroupType = group.useMetrics
                ? ratingGroupTypes.find(r => r.ref === 'Metrics').id
                : ratingGroupTypes.find(r => r.ref === 'Text').id;
            apiPatch('competency-rating-groups', group, user.guid, () => {
                dispatch(setShowSuccess(translations.success));
                api.load();
                callback?.();
            });
        },
        remove: (ids, callback) => {
            if (!Array.isArray(ids)) ids = [ids];
            ids.forEach((id, idx) => {
                apiDelete(`competency-rating-groups/${id}`, null, user.guid, () => {
                    if (idx + 1 === ids.length) {
                        api.load();
                        callback?.();
                    }
                });
            });
        },
    };

    React.useEffect(() => {
        api.load();
    }, []);

    const options = {
        ...DefaultOptions(),
        label: translations.title,
        labelPlural: translations.titlePlural,
    };

    const tableColumns = [
        {
            label: translations.columnName,
            dataProperty: 'name',
        },
        {
            label: translations.columnType,
            dataProperty: 'ratingGroupType',
        },
    ];

    if (loading) {
        return <Loading />;
    }

    return (
        <div>
            <SimpleAdmin
                data={data}
                defaultValues={{ range1: '0' }}
                fields={formFields}
                options={options}
                columns={tableColumns}
                remove={api.remove}
                add={api.save}
                update={api.update}
            />
        </div>
    );
};

export default AdminCompetencyRatingGroups;
